import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import './CapitalAllocationDonut.css';

interface CapitalAllocationData {
  name: string;
  value: number;
  color: string;
}

interface CapitalAllocationDonutProps {
  data: CapitalAllocationData[];
}

const CapitalAllocationDonut: React.FC<CapitalAllocationDonutProps> = ({ data }) => {
  // Filter out items with zero or negative values
  const filteredData = data.filter(item => item.value > 0);

  // Determine chart dimensions based on screen size
  const [chartDimensions, setChartDimensions] = React.useState({
    height: 250,
    cy: 125,
    innerRadius: 60,
    outerRadius: 100
  });

  React.useEffect(() => {
    const updateDimensions = () => {
      const width = window.innerWidth;
      if (width <= 480) {
        // Small mobile
        setChartDimensions({
          height: 180,
          cy: 90,
          innerRadius: 40,
          outerRadius: 70
        });
      } else if (width <= 768) {
        // Tablet/large mobile
        setChartDimensions({
          height: 200,
          cy: 100,
          innerRadius: 50,
          outerRadius: 80
        });
      } else {
        // Desktop
        setChartDimensions({
          height: 250,
          cy: 125,
          innerRadius: 60,
          outerRadius: 100
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = payload[0].payload.total;
      const percent = ((data.value / total) * 100).toFixed(1);

      return (
        <div style={{
          background: '#1e293b',
          border: '1px solid #8b5cf6',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
        }}>
          <p style={{ fontWeight: 'bold', marginBottom: '4px', color: '#f1f5f9' }}>
            {data.name}
          </p>
          <p style={{ color: data.payload.fill, fontWeight: 600, margin: '4px 0' }}>
            {formatCurrency(data.value)}
          </p>
          <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>
            {percent}%
          </p>
        </div>
      );
    }
    return null;
  };

  const total = filteredData.reduce((sum, item) => sum + item.value, 0);
  const dataWithTotal = filteredData.map(item => ({ ...item, total }));

  return (
    <div className="donut-chart-container">
      <div className="donut-chart-wrapper">
        <ResponsiveContainer width="100%" height={chartDimensions.height}>
          <PieChart>
            <Pie
              data={dataWithTotal}
              cx="50%"
              cy={chartDimensions.cy}
              innerRadius={chartDimensions.innerRadius}
              outerRadius={chartDimensions.outerRadius}
              dataKey="value"
              paddingAngle={2}
            >
              {dataWithTotal.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="donut-legend-wrapper">
        <div className="donut-legend">
          {dataWithTotal.map((entry, index) => {
            const percent = ((entry.value / total) * 100).toFixed(1);
            return (
              <div key={`legend-${index}`} className="donut-legend-item">
                <div
                  className="donut-legend-dot"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="donut-legend-text">
                  {entry.name}: {formatCurrency(entry.value)} ({percent}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CapitalAllocationDonut;
